import { FormEvent, useState } from 'react';
import { CanonicalWine, QuickAddFormData } from '../types';

interface QuickAddModalProps {
  wine: CanonicalWine;
  onSubmit: (wine: CanonicalWine, formData: QuickAddFormData) => void;
  onBack: () => void;
  onClose: () => void;
}

const initialFormData: QuickAddFormData = {
  quantity: 1,
  purchase_price: '',
  cellar_location: '',
  my_rating: '',
  tasting_notes: ''
};

export function QuickAddModal({ wine, onSubmit, onBack, onClose }: QuickAddModalProps) {
  const [formData, setFormData] = useState<QuickAddFormData>(initialFormData);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    onSubmit(wine, formData);
  };

  const getColorClass = (color?: string) => {
    switch (color) {
      case 'red': return 'wine-color-red';
      case 'white': return 'wine-color-white';
      case 'rosé': return 'wine-color-rose';
      case 'orange': return 'wine-color-orange';
      default: return 'wine-color-red';
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div 
        className="ios-sheet w-full"
        style={{ height: '90vh', display: 'flex', flexDirection: 'column' }}
        onClick={e => e.stopPropagation()}
      >
        <div className="ios-sheet-handle" />

        {/* Header */}
        <div style={{ 
          padding: '0 20px 12px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <button
            onClick={onBack}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--color-wine)',
              fontSize: '16px',
              cursor: 'pointer'
            }}
          >
            ← Back
          </button>
          <h2 className="wine-title" style={{ 
            fontSize: '18px', 
            margin: 0,
            color: 'var(--color-text)'
          }}>
            Add to Cellar
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--color-wine)',
              fontSize: '16px',
              cursor: 'pointer'
            }}
          >
            Cancel
          </button>
        </div>

        {/* Scrollable Content */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {/* Wine Info Card */}
          <div className="ios-card" style={{ margin: '0 16px 16px', padding: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
              <div className={`wine-color-bar ${getColorClass(wine.color)}`} style={{ height: '50px' }} />
              <div style={{ flex: 1 }}>
                <h3 className="wine-title" style={{ 
                  fontSize: '18px', 
                  color: 'var(--color-wine)',
                  marginBottom: '4px'
                }}>
                  {wine.wine_name} {wine.vintage && <span style={{ fontWeight: 300 }}>{wine.vintage}</span>}
                </h3>
                <p style={{ fontSize: '15px', color: 'var(--color-text)', marginBottom: '8px' }}>
                  {wine.producer}
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', fontSize: '13px', color: 'var(--color-text-muted)' }}>
                  {wine.varietal && <span>{wine.varietal}</span>}
                  {wine.region && <span>• {wine.region}</span>}
                  {wine.country && <span>• {wine.country}</span>}
                </div>
                {wine.vivino_rating && (
                  <div style={{ marginTop: '8px', fontSize: '14px', color: 'var(--color-wine)' }}>
                    <strong>{wine.vivino_rating.toFixed(1)}</strong> ★ Vivino
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} style={{ padding: '0 16px' }}>
            <p className="form-section-title">Your Details</p>
            
            {/* Quantity */}
            <div style={{ marginBottom: '20px' }}>
              <label className="ios-label">Quantity</label>
              <div className="quantity-stepper" style={{ justifyContent: 'flex-start', marginTop: '8px' }}>
                <button
                  type="button"
                  onClick={() => setFormData(f => ({ ...f, quantity: Math.max(1, f.quantity - 1) }))}
                  className="quantity-btn"
                >
                  −
                </button>
                <span className="wine-title" style={{ 
                  fontSize: '28px', 
                  color: 'var(--color-wine)',
                  width: '50px',
                  textAlign: 'center'
                }}>
                  {formData.quantity}
                </span>
                <button
                  type="button"
                  onClick={() => setFormData(f => ({ ...f, quantity: f.quantity + 1 }))}
                  className="quantity-btn"
                >
                  +
                </button>
                <span style={{ fontSize: '14px', color: 'var(--color-text-muted)', marginLeft: '8px' }}>
                  bottles
                </span>
              </div>
            </div>

            {/* Price */}
            <div style={{ marginBottom: '16px' }}>
              <label className="ios-label">Purchase Price (per bottle)</label>
              <input
                type="number"
                value={formData.purchase_price}
                onChange={(e) => setFormData(f => ({ ...f, purchase_price: e.target.value }))}
                placeholder="$"
                className="ios-input"
              />
            </div>

            {/* Location */}
            <div style={{ marginBottom: '16px' }}>
              <label className="ios-label">Cellar Location</label>
              <input
                type="text"
                value={formData.cellar_location}
                onChange={(e) => setFormData(f => ({ ...f, cellar_location: e.target.value }))}
                placeholder="e.g. Rack A, Shelf 2"
                className="ios-input"
              />
            </div>

            {/* My Rating */}
            <div style={{ marginBottom: '16px' }}>
              <label className="ios-label">My Rating (1-100)</label>
              <input
                type="number"
                min="1"
                max="100"
                value={formData.my_rating}
                onChange={(e) => setFormData(f => ({ ...f, my_rating: e.target.value }))}
                placeholder="Optional"
                className="ios-input"
              />
            </div>

            {/* Notes */}
            <div style={{ marginBottom: '16px' }}>
              <label className="ios-label">Notes</label>
              <textarea
                value={formData.tasting_notes}
                onChange={(e) => setFormData(f => ({ ...f, tasting_notes: e.target.value }))}
                placeholder="Your notes about this wine..."
                rows={3}
                className="ios-input"
                style={{ resize: 'none' }}
              />
            </div>
          </form>
        </div>

        {/* Submit Button */}
        <div style={{ 
          padding: '16px 20px', 
          borderTop: '1px solid var(--color-border)',
          background: 'var(--color-card)'
        }}>
          <button
            onClick={handleSubmit}
            className="ios-btn ios-btn-primary w-full"
            style={{ fontSize: '17px' }}
          >
            Add {formData.quantity} Bottle{formData.quantity > 1 ? 's' : ''} to Cellar
          </button>
        </div>
      </div>
    </div>
  );
}
